import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { edgesToNodes } from 'utils/storefront/format-resource';
import { IconPlus, IconMinus } from 'utils/icon';
import ColorSwatches from './color-swatches';
import OptionSelector from './option-selector';

const VariantSelector = ({
  option,
  product,
  handleOptionChange,
  setActiveProduct,
  chartPopup,
  selectedOptions,
  activeProduct,
  inventoryMessage,
  sizeLimit,
  colorLimit,
  currentVariant,
  productGroups,
  setQty,
  isStylitics,
  qty,
}) => {
  const optionId = `product-option-${option.name}`;
  const [availableValues, setAvailableValues] = useState([]);
  const [activeColor, setActiveColor] = useState(
    (
      (product.options || []).find((opt) => opt.name === 'Color')?.values[0] ||
      (product.optionsWithValues || []).find((opt) => opt.name === 'Color')
        ?.selected_value ||
      ''
    ).toLowerCase(),
  );
  const [showError, setShowError] = useState(false);
  const disableQuantity = currentVariant?.currentlyNotInStock;

  const handleSwatchClick = (colorData) => {
    const newProduct = edgesToNodes(productGroups, 'product')?.find(
      (prod) => prod.options[0].values[0] === colorData.name,
    );

    setActiveProduct(newProduct);
  };

  useEffect(() => {
    setActiveColor(
      (
        (activeProduct.options || []).find((opt) => opt.name === 'Color')
          ?.values[0] ||
        (activeProduct.optionsWithValues || []).find(
          (opt) => opt.name === 'Color',
        )?.selected_value ||
        ''
      ).toLowerCase(),
    );
  }, [activeProduct]);

  useEffect(() => {
    const filteredVariants = (
      product.variants?.edges || product.variants
    ).filter((v) => (v?.node || v)?.availableForSale);

    setAvailableValues(
      filteredVariants.map(
        (v) =>
          (v?.node || v)?.selectedOptions.find(
            (o) => o.name.toLowerCase() === option.name.toLowerCase(),
          )?.value,
      ) || [],
    );
  }, [product]);

  const handleDecreaseQuantity = () => {
    if (qty === 1) return;
    setQty(qty - 1);
    setShowError(false);
  };

  const handleIncreaseQuantity = () => {
    if (!currentVariant?.availableForSale) {
      if (qty >= currentVariant.quantityAvailable) {
        setShowError(true);
        return;
      }
      if (qty + 1 === currentVariant.quantityAvailable) setShowError(true);
      else setShowError(false);
    }
    setQty(qty + 1);
  };

  const handleQuantityChange = (e) => {
    if (
      parseInt(e.currentTarget.value, 10) > currentVariant?.quantityAvailable
    ) {
      setQty(currentVariant.quantityAvailable);
      setShowError(true);
    } else {
      setQty(e.currentTarget.value);
      setShowError(false);
    }
  };

  const availableColorsArray = product?.availableColors?.value
    ? JSON.parse(product?.availableColors?.value)
    : product?.metafields?.availableColors;

  return (option.name?.toLowerCase() || option.toLowerCase()) === 'color' ? (
    <div className="pt-4 pb-5 border-b border-gray-300">
      <p className="body-3 mb-2.5">
        {availableColorsArray?.length < 1
          ? window.TMW.Strings.product.selectSingleColor
          : window.TMW.Strings.product.selectColor}
        : <span className="font-medium capitalize">{activeColor}</span>
      </p>
      <ColorSwatches
        product={product}
        isDisabled={!productGroups?.length}
        availableColorsArray={availableColorsArray || []}
        onClickSwatch={handleSwatchClick}
        maxSwatches={colorLimit || 100}
        activeColor={activeColor}
        align="justify-start flex-wrap"
        swatchSizes="w-6 h-6"
      />
    </div>
  ) : (
    <div className="pt-4">
      {option.values.length > 1 ? (
        <OptionSelector
          option={option}
          selectedOptions={selectedOptions}
          availableValues={availableValues}
          handleOptionChange={handleOptionChange}
          isStylitics={isStylitics}
          sizeLimit={sizeLimit}
          optionId={optionId}
          inventoryMessage={inventoryMessage}
          product={product}
          chartPopup={chartPopup}
          currentVariant={currentVariant}
        />
      ) : (
        <div className="flex flex-wrap gap-x-3 items-center">
          <input
            className="hidden peer"
            name={`${product.id}_${optionId}`}
            id={`0_${product.id}_${optionId}-${option.values[0]}`}
            value={option.values[0]}
            type="radio"
            checked={true}
            onClick={handleOptionChange}
            data-option-name={option.name}
            data-value={option.values[0]}
          />
          <span className="body-3 text-text-default">
            {window.TMW.Strings.product.quantity}
          </span>
          <div
            className={`flex items-center border border-[#DDD]
            w-[74px] py-1.5 px-2.5 justify-between text-gray-900`}
          >
            <button
              type="button"
              onClick={handleDecreaseQuantity}
              className="disabled:text-gray-600"
              disabled={qty === 1}
            >
              <IconMinus />
            </button>
            <input
              type="number"
              max={currentVariant?.quantityAvailable}
              min="1"
              value={qty}
              onChange={handleQuantityChange}
              className="no-spinner border-none bg-transparent p-0 text-center body-3 text-text-default"
            />
            <button
              type="button"
              onClick={handleIncreaseQuantity}
              className="disabled:text-gray-600"
              disabled={disableQuantity}
            >
              <IconPlus />
            </button>
          </div>
          {showError && (
            <p className="w-full body-3 text-text-promotion mt-1">
              {window.TMW.Strings.product.inventoryError.replace(
                '{qty}',
                currentVariant.quantityAvailable,
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VariantSelector;
